import { Component, Inject, OnDestroy } from '@angular/core';

import { GalleryService } from 'app/gallery/gallery.service';
import { ImagePlaceholder
       } from 'collage-designer/stage/placeholder/image-placeholder';
import { PlaceholderService
       } from 'collage-designer/stage/placeholder/placeholder.service';
import { Subscription } from 'rxjs';
import { Utils } from '../../core/utils';
import { ToolboxComponent } from '../toolbox.component';
import { ToolboxService } from '../toolbox.service';

@Component({
    selector: 'gallery-toolbox',
    templateUrl: './gallery-toolbox.component.html',
    styleUrls: ['../toolbox.component.scss'],
    standalone: false
})
export class GalleryToolboxComponent extends ToolboxComponent
    implements OnDestroy
{
  public imagesCount: number = 0;

  protected name: string = 'GalleryToolbox';

  private subscriptions: Subscription = new Subscription();

  constructor(
      @Inject(ToolboxService) toolboxService,
      @Inject(GalleryService) private galleryService,
      @Inject(PlaceholderService) private placeholderService)
  {
    super(toolboxService);

    this.subscriptions.add(
        this.galleryService.imageStockChanged.subscribe(() => {
            this.imagesCount = this.galleryService.getImages().length;
        }));

    this.imagesCount = this.galleryService.getImages().length;
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
  }

  public btnCloseClick(): void
  {
    super.btnCloseClick();
    this.galleryService.setSelectedImageId(undefined);
    this.removeHighlighter(this.placeholderService.getImagePlaceholders(true));
  }

  public async handleImageClick(imageId: string): Promise<void>
  {
    if (!Utils.isLargeDevice() &&
        this.galleryService.isImageUsed(imageId) &&
        this.placeholderService.getFocusedPlaceholder()) {
      await this.swapImage(imageId);
    }
    else if (this.galleryService.isImageSelected(imageId)) {
      this.unselectImage();
    }
    else if (!this.galleryService.isImageUsed(imageId)) {
      await this.changeImage(imageId);
    }
  }

  private async changeImage(imageId: string): Promise<void>
  {
    if (this.placeholderService.getFocusedPlaceholder()
        instanceof ImagePlaceholder) {
      await this.placeholderService.getFocusedPlaceholder()
        .loadGalleryImage(imageId);
      this.closeIfSmallOrMediumDevice();
      this.placeholderService.getFocusedPlaceholder().clickEvent();
    }
    else {
      const placeholders = this.placeholderService.getImagePlaceholders(true);

      if (placeholders.length === 1) {
        const placeholder = placeholders[0];
        await placeholder.loadGalleryImage(imageId);
        this.closeIfSmallOrMediumDevice();
        placeholder.clickEvent();
      }
      else if (placeholders.length > 1) {
        this.placeholderService.disableTextPlaceholders();
        this.galleryService.setSelectedImageId(imageId);
        this.addHighlighter(placeholders);

        if (!Utils.isLargeDevice()) {
          super.btnCloseClick();
        }
      }
    }
  }

  private async swapImage(imageId: string): Promise<void>
  {
    const placeholders = this.placeholderService.getImagePlaceholders(true);
    const focusedPlaceholder = this.placeholderService.getFocusedPlaceholder();
    const focusedPhImage = focusedPlaceholder.getImageModel();

    const currentImageIdPlaceholder =
        placeholders.find(ph => ph.getImageModel()?.id === imageId);

    if (!currentImageIdPlaceholder ||
       (focusedPhImage !== undefined && focusedPhImage.id === imageId)) {
      return;
    }

    const currentImageIdPlaceholderLoad = focusedPhImage ?
        currentImageIdPlaceholder.loadGalleryImage(focusedPhImage.id) :
        currentImageIdPlaceholder.reset();

    await Promise.all([
      focusedPlaceholder.loadGalleryImage(imageId),
      currentImageIdPlaceholder]);

    this.closeIfSmallOrMediumDevice();
  }

  public async resetImagePlaceholder(imageId: string): Promise<void>
  {
      const placeholders = this.placeholderService.getImagePlaceholders(true);
      const placeholder = placeholders.find(ph => {
        const imgModel = ph.getImageModel();

        if (!imgModel) {
          return false;
        }

        return imgModel.id === imageId;
      });

      if (!placeholder) {
        console.error('could not find placeholder with image id: ' + imageId);
        return;
      }

      await placeholder.reset();
  }

  private addHighlighter(placeholders: Array<ImagePlaceholder>): void
  {
    for (let placeholder of placeholders) {
      placeholder.showPlaceholderHighlighter();
    }
  }

  private removeHighlighter(placeholders: Array<ImagePlaceholder>): void
  {
    for (let placeholder of placeholders) {
      placeholder.hidePlaceholderHighlighter();
    }

    this.placeholderService.enableTextPlaceholders();
  }

  private closeIfSmallOrMediumDevice(): void
  {
    if (!Utils.isLargeDevice()) {
      this.btnCloseClick();
    }
  }

  private unselectImage(): void
  {
    this.galleryService.setSelectedImageId(undefined);
    this.removeHighlighter(this.placeholderService.getImagePlaceholders(true));
  }
}
